import { DEFAULT_OVERLAY, type OverlayConfig } from './overlay'
import type { Annotation, AudioClip } from './recordingOverlay'

export type RtspTransport = 'tcp' | 'udp'
/**
 * Where the stream's audio comes from. 'file' plays a local track on a loop —
 * background music for a camera wall that has no useful sound of its own.
 */
export type AudioMode = 'silent' | 'camera' | 'file' | 'folder'
export type VideoMode = 'copy' | 'encode'
export type VideoEncoder =
  | 'libx264'
  | 'h264_qsv'
  | 'h264_nvenc'
  | 'h264_amf'
  | 'h264_videotoolbox'
export type MosaicLayout = 'auto' | '1x1' | '2x1' | '2x2' | '3x2' | '3x3' | '4x2' | '4x3'
/** How a camera's frame is fitted into its tile: letterbox it, or crop it to fill. */
export type TileFit = 'contain' | 'cover'

/**
 * Which edge a cover crop protects. Cameras burn their clock into the top
 * (sometimes bottom) edge, and a centred crop eats exactly that.
 */
export type TileFitAnchor = 'top' | 'center' | 'bottom'
/** grid = equal tiles; spotlight = one full-frame camera with the rest as a thumbnail strip. */
export type MosaicMode = 'grid' | 'spotlight'

/**
 * 'auto' lets ffmpeg pick whatever this machine offers, which is the right
 * answer almost everywhere; the explicit ones exist for a box where auto picks
 * badly. Windows sites are usually d3d11va (any GPU) or qsv (Intel iGPU).
 */
export type HwDecode = '' | 'auto' | 'd3d11va' | 'qsv' | 'cuda' | 'videotoolbox'

/**
 * Outcome of trying to put an overlay edit on air without a restart.
 *
 * `live` true means the running encoder took it between frames. `applied`
 * false with a reason means the graph cannot be commanded into that shape and
 * the caller should offer a restart — never silently swallow it, or the
 * Designer would show something the broadcast is not doing.
 */
export interface LiveApplyResult {
  applied: boolean
  live: boolean
  reason: string | null
  commands?: number
}

/**
 * What this machine is, for an operator who is not standing in front of it.
 * Lives here rather than beside its collector because the preload and the
 * renderer both need the shape, and neither may reach into main.
 */
export interface SystemInfo {
  computerName: string
  os: string
  arch: string
  /** How long the machine has been up — a box that reboots nightly explains a lot. */
  machineUptimeSec: number
  totalMemoryGb: number
  appVersion: string
  /** 'Desktop app' or 'Headless daemon', so a screenshot is unambiguous. */
  runningAs: string
  dataDir: string
  configFile: string
  ffmpegPath: string
  ffmpegVersion: string | null
  /** Every non-loopback IPv4 — the addresses the headless UI answers on. */
  addresses: string[]
  /** Where the daemon is listening, when this is one. */
  listeningOn: string | null
  cloudDeviceName: string
  cloudBaseUrl: string
  /** Has a device token: this box registered itself with the control plane. */
  enrolled: boolean
  /**
   * An operator ticked it off in the console. This — not enrolment — is what
   * lets the box go on air, and the two being confused is exactly why the panel
   * shows both: a device can be enrolled for weeks and still be refused.
   * null when the control plane has not been reached yet.
   */
  approved: boolean | null
}

/** A spotlight mini placed by hand: where it sits on the output canvas, in pixels. */
export interface ThumbRect {
  x: number
  y: number
  w: number
  h: number
}
export type StripPosition = 'top' | 'bottom' | 'left' | 'right'
/** Where the run of thumbnails sits along the strip's long axis. */
export type StripAlign = 'start' | 'middle' | 'end'
/** Corner for the audio level display, or 'off'. */
export type AudioVizPosition = 'off' | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight'
/** Footprint of the level display. Sizes are in 1080p pixels and scale with the canvas. */
export type AudioVizSize = 'small' | 'medium' | 'large'
/** Where the tile block (and a partial last row) sits on the canvas. */
export type GridAlignH = 'left' | 'center' | 'right'
export type GridAlignV = 'top' | 'center' | 'bottom'
export type Preset =
  | 'ultrafast'
  | 'superfast'
  | 'veryfast'
  | 'faster'
  | 'fast'
  | 'medium'
/** Which ingest a destination points at; drives the URL prefill and the backup option. */
export type DestinationKind = 'youtube' | 'facebook' | 'custom'
export type ThemeMode = 'light' | 'dark'

export const ENCODER_LABELS: Record<VideoEncoder, string> = {
  libx264: 'libx264 (CPU)',
  h264_qsv: 'h264_qsv (Intel Quick Sync)',
  h264_nvenc: 'h264_nvenc (NVIDIA)',
  h264_amf: 'h264_amf (AMD)',
  h264_videotoolbox: 'h264_videotoolbox (Apple)'
}

/**
 * Where a camera's frames come from: an RTSP camera, a capture device on this
 * machine, a video file looped forever (a slate, a promo reel, a recorded
 * walkthrough slotted into the wall like any live source), an online stream
 * URL — HLS, DASH, a plain MP4 over HTTP, SRT, anything ffmpeg opens directly —
 * or a still picture shown as an endless camera (a map, a notice board, a
 * station diagram holding one tile of the wall).
 *
 * 'url' is not a YouTube or Facebook page: those serve HTML, and ffmpeg reports
 * "Invalid data found" on one. Extracting a playable URL from a watch page needs
 * yt-dlp, which is a separate tool this app does not ship.
 */
export type CameraKind = 'rtsp' | 'device' | 'file' | 'url' | 'picture'

export interface CameraSource {
  id: string
  /** Drawn on the tile when labels are on. */
  name: string
  /** RTSP url with credentials, or for kind 'file' the video file's path. */
  url: string
  enabled: boolean

  kind: CameraKind
  /**
   * Capture device identifier, in whatever form the platform's ffmpeg input wants:
   * a DirectShow device name on Windows, an AVFoundation index on macOS, a
   * /dev/videoN path on Linux.
   */
  device: string
  /** Optional companion audio device on the same input; empty means video only. */
  audioDevice: string
  /** Force a capture mode, e.g. '1280x720'. Empty lets ffmpeg pick the default. */
  deviceSize: string
  /** Force a capture frame rate. 0 lets ffmpeg pick the default. */
  deviceFps: number
  /**
   * How this source fills its box in spotlight mode, overriding the defaults —
   * minis default to 'cover' (a letterboxed camera mini reads as a glitch), the
   * background to the global tileFit. '' means those defaults; 'contain' keeps
   * a placed map or logo whole; 'stretch' fills ignoring aspect. Grid mode
   * keeps the one global Fit for every tile.
   */
  fit: CameraFit
}

export type CameraFit = '' | 'contain' | 'cover' | 'stretch'

/** One file in a directory listing from the box (see main/fsBrowse). */
export interface BrowseEntry {
  name: string
  /** Bytes; -1 when stat failed (still selectable — ffmpeg is the judge). */
  size: number
}

/** A directory on the machine the streamer runs on, for the daemon UI's picker. */
export interface BrowseResult {
  path: string
  /** Null at a filesystem root. */
  parent: string | null
  /** Quick jumps: home, and on Windows every drive that answers. */
  roots: string[]
  dirs: string[]
  files: BrowseEntry[]
  error: string | null
}

/** Result of one file-manager operation (mkdir/rename/delete/upload). */
export interface FsOpResult {
  ok: boolean
  error: string | null
}

/** One entry from ffmpeg's device enumeration. */
export interface CaptureDevice {
  /** Passed to ffmpeg verbatim. */
  id: string
  label: string
  kind: 'video' | 'audio'
}

/** Single place new cameras are created, so a new field cannot be forgotten in one of them. */
export function blankCamera(id: string, name: string, patch: Partial<CameraSource> = {}): CameraSource {
  return {
    id,
    name,
    url: '',
    enabled: true,
    kind: 'rtsp',
    device: '',
    audioDevice: '',
    deviceSize: '',
    deviceFps: 0,
    fit: '',
    ...patch
  }
}

/** A camera is usable once it has the identifier its kind needs. */
export function cameraReady(camera: CameraSource): boolean {
  // 'file' and 'url' share the url field with 'rtsp', so all three just need it
  // filled; only a capture device is identified by something else.
  return camera.kind === 'device' ? camera.device.trim() !== '' : camera.url.trim() !== ''
}

/** One RTMP endpoint the wall is pushed to. Several are muxed with ffmpeg's tee. */
/**
 * Per-stream encoding overrides. A null field inherits the global setting, so the
 * common case — every stream the same — stays a single place to edit, while a
 * 720p single-camera feed alongside a 1080p wall costs four numbers.
 */
export interface DestinationOverrides {
  outputWidth: number | null
  outputHeight: number | null
  videoBitrateKbps: number | null
  fps: number | null
}

export interface StreamDestination {
  id: string
  /** Shown in the UI and in "destination down" alerts. */
  name: string
  kind: DestinationKind
  /** Ingest endpoint without the stream key. */
  url: string
  streamKey: string
  enabled: boolean
  /** YouTube only: push the same feed to the backup ingest as well (YouTube's own redundancy). */
  useBackup?: boolean

  /**
   * Which camera this stream carries.
   *
   * `null` means the composed wall of every enabled camera — the original
   * behaviour, and still the default. An id streams that camera alone, full
   * frame, which is how one machine feeds several YouTube channels at once from
   * different cameras.
   *
   * An id that no longer matches an enabled camera falls back to the wall rather
   * than going dark: a deleted camera should not silently take a channel off air.
   */
  cameraId: string | null

  /**
   * Split the broadcast into segments this many hours long. 0 keeps one
   * continuous stream, which is the default and the previous behaviour.
   *
   * At the end of a segment the push stops for `segmentGapSec` and reconnects.
   * The gap is the point: YouTube resumes the *same* broadcast after a short
   * reconnect, so only a gap long enough to trip its auto-stop produces a new
   * video. Whether that happens at all depends on the channel having
   * auto-start/auto-stop enabled — the client cannot force it.
   */
  segmentHours: number

  /**
   * Roll segments over through the YouTube Data API instead of dropping the
   * push: the next video is created and taken live while ffmpeg keeps
   * streaming, then the old one is completed — no off-air gap, and no reliance
   * on YouTube noticing a disconnect. Needs a signed-in YouTube account, and
   * only means anything on a youtube destination with segments.
   */
  apiRollover?: boolean
  /** Title for each API-created video; {name} {date} {time} are filled in. */
  broadcastTitle?: string
  /** Privacy of each API-created video. Absent means public. */
  broadcastPrivacy?: 'public' | 'unlisted' | 'private'

  /** Null fields inherit the global encoding settings. */
  overrides: DestinationOverrides
}

/** When a chat rule fires. */
export type ChatRuleMode = 'change' | 'above' | 'below' | 'interval'

/**
 * One automatic live-chat post, driven by a metric value.
 *
 * 'change' posts when the value moves by at least `threshold` from the last
 * posted value (0 = any change). 'above'/'below' post once on crossing the
 * threshold, not continuously while past it. 'interval' posts the current
 * value every `threshold` minutes regardless of movement.
 */
export interface ChatRule {
  id: string
  enabled: boolean
  /** Metric tag(s) the MESSAGE carries — comma-separated for several readings. */
  tag: string
  /**
   * The single tag the CONDITION watches. Empty falls back to the first
   * message tag — the pre-split behaviour, and still the sensible default.
   * Kept separate because "alert when the water level crosses 61" and "tell
   * the chat the volt and amp when it does" name different tags.
   */
  watchTag?: string
  mode: ChatRuleMode
  threshold: number
  /** {values} {valueN} {tag} {value} {date} {time} are filled in. */
  template: string
  /** Floor between posts of this rule, seconds. Chat costs real API quota. */
  minIntervalSec: number
}

/**
 * One viewer-triggered reply: a chat message containing `trigger`
 * (case-insensitive) answers with the metric's current value.
 */
export interface ChatCommand {
  id: string
  enabled: boolean
  /** Phrase to look for in viewer messages, e.g. "Get L1 data". */
  trigger: string
  /**
   * Metric tag(s) the reply carries — comma-separated for a multi-reading
   * answer ("metric:VOLT, metric:AMP" → "VOLT=230, AMP=12").
   */
  tag: string
  /** {user} {tag} {value} {time} are filled in. Empty = a sensible default. */
  template: string
  /** Floor between replies to this command, seconds — viewers can spam. */
  cooldownSec: number
}

export interface ChatAlertsConfig {
  enabled: boolean
  rules: ChatRule[]
  /** Read the live chat and answer viewer commands. Costs quota per poll. */
  commandsEnabled?: boolean
  /** How often the chat is polled, seconds. */
  commandPollSec?: number
  commands?: ChatCommand[]
}

export const DEFAULT_CHAT_ALERTS: ChatAlertsConfig = {
  enabled: false,
  rules: []
}

/** Sign-in state of the YouTube Data API account, shown in the UI. */
export interface YoutubeStatus {
  /** OAuth client id + secret are set. */
  configured: boolean
  signedIn: boolean
  /** Channel title of the signed-in account. */
  channel: string | null
  clientId: string
  error: string | null
}

export const NO_OVERRIDES: DestinationOverrides = {
  outputWidth: null,
  outputHeight: null,
  videoBitrateKbps: null,
  fps: null
}

export const DESTINATION_PRESETS: Record<DestinationKind, { label: string; url: string }> = {
  youtube: { label: 'YouTube Live', url: 'rtmp://a.rtmp.youtube.com/live2' },
  // rtmps needs a TLS-enabled ffmpeg build (the gyan.dev Windows builds have one).
  facebook: { label: 'Facebook Live', url: 'rtmps://live-api-s.facebook.com:443/rtmp' },
  custom: { label: 'Custom RTMP', url: '' }
}

/** YouTube's documented secondary ingest — used verbatim, the key is appended like the primary. */
export const YOUTUBE_BACKUP_URL = 'rtmp://b.rtmp.youtube.com/live2?backup=1'

export interface TelegramAlerts {
  enabled: boolean
  botToken: string
  chatId: string
}

export interface DiscordAlerts {
  enabled: boolean
  webhookUrl: string
}

export interface WebhookAlerts {
  enabled: boolean
  url: string
}

export interface AlertsConfig {
  telegram: TelegramAlerts
  discord: DiscordAlerts
  webhook: WebhookAlerts
  /** How long the stream must stay down before a "down" alert fires. Rides through fast restarts. */
  downThresholdSec: number
  /** Minimum gap between two alerts of the same kind. */
  cooldownSec: number
  /** Also announce user-initiated start/stop, not just outages. */
  notifyManualStartStop: boolean
}

export interface StreamConfig {
  cameras: CameraSource[]
  rtspTransport: RtspTransport
  /**
   * GPU-assisted decoding of the camera inputs, or '' for software.
   *
   * Decode cost is what a camera wall spends most of its CPU on, and H.265
   * costs several times what H.264 does — a site that ran four cameras fine
   * fell to 0.14x realtime when more were added, the decoder starved, RTSP
   * packets were dropped and the log filled with unreconstructable frames.
   * Offloading decode to the GPU is the one change that moves that number.
   * Frames still come back to system memory, so every existing filter works
   * unchanged.
   */
  hwDecode: HwDecode
  /** How multiple cameras share the canvas. */
  mode: MosaicMode
  /** Grid arrangement when more than one camera is enabled and mode is 'grid'. */
  layout: MosaicLayout
  /** Alignment of the tile block within the canvas, and of a partial last row. */
  gridAlignH: GridAlignH
  gridAlignV: GridAlignV

  /** Which enabled camera fills the background in spotlight mode. */
  mainCameraIndex: number
  /** Edge the thumbnail strip sits on in spotlight mode. */
  stripPosition: StripPosition
  /**
   * Alignment of the thumbnails along that edge. 'middle' is the original
   * behaviour; 'start'/'end' pack them against one end, inside the margin.
   */
  stripAlign: StripAlign
  /** Thumbnail size as a fraction of the canvas along the strip's cross axis. */
  thumbScale: number
  /** Gap between thumbnails, and inset from the canvas edge, in pixels. */
  thumbGap: number
  thumbMargin: number
  /** Outline drawn around each thumbnail so it reads against the background, in pixels. */
  thumbBorder: number
  /**
   * Hand-placed spotlight minis, keyed by camera id: position and size on the
   * output canvas in pixels. A camera with no entry stays in the automatic
   * strip; clearing the map returns everything to it. Kept by id rather than
   * position so reordering or disabling other cameras cannot move a mini.
   */
  spotlightRects: Record<string, ThumbRect>
  /**
   * Mini layering in spotlight mode: camera ids from bottom to top. Later ids
   * draw over earlier ones when minis overlap, and the automatic strip seats
   * its minis in this sequence too. Ids not listed keep the camera-list order,
   * below every listed one; the main camera's entry is simply ignored.
   */
  spotlightOrder: string[]
  /** Mosaic canvas size; ignored when a single camera streams straight through. */
  outputWidth: number
  outputHeight: number
  tileFit: TileFit
  /** cover only: which edge survives the crop. */
  tileFitAnchor: TileFitAnchor
  showLabels: boolean

  /** Every endpoint the wall is pushed to; two or more are muxed with ffmpeg's tee. */
  destinations: StreamDestination[]

  videoMode: VideoMode
  encoder: VideoEncoder
  /** libx264 only; hardware encoders use their own rate control. */
  preset: Preset
  videoBitrateKbps: number
  bufsizeKbps: number
  /** Keyframe interval in frames. YouTube wants a keyframe at least every 2s. */
  gop: number
  fps: number

  audioMode: AudioMode
  /** Index into the enabled cameras when audioMode is 'camera'. */
  audioCameraIndex: number
  /** Absolute path to an audio file, used when audioMode is 'file'. */
  audioFilePath: string
  /**
   * Folder of audio files played in order and then repeated from the top, used
   * when audioMode is 'folder'. Sorted by filename so the running order is
   * predictable and can be controlled by naming (01-, 02-, …).
   */
  audioFolderPath: string
  /**
   * Corner for the animated level display, or 'off' to draw nothing.
   *
   * Suppressed automatically when there is no real audio: a silent track would
   * render a flat bar, which reads as "the music has stopped" rather than "there
   * is no music".
   */
  audioVizPosition: AudioVizPosition
  /** How large the level display is drawn. */
  audioVizSize: AudioVizSize
  /**
   * Overall opacity of the level display, 0.1–1.
   *
   * Scales the bars and the backing panel together, so 1 is the solid look and
   * lower values let the picture through. The panel stays proportionally fainter
   * than the bars at every setting — it is a backdrop, and matching their opacity
   * would leave a grey slab competing with the thing it exists to make readable.
   */
  audioVizOpacity: number
  audioBitrateKbps: number
  sampleRate: number

  /** Restart ffmpeg automatically when it exits unexpectedly. */
  autoRestart: boolean
  /** Base reconnect delay; consecutive failures back off exponentially from here. */
  restartDelaySec: number
  /**
   * How long the push stays down between segments. Long enough that YouTube ends
   * the broadcast rather than treating it as a brief dropout — 90s is a starting
   * point, and the right value has to be confirmed against the channel.
   */
  segmentGapSec: number
  /** Ceiling for the exponential backoff. */
  maxRestartDelaySec: number
  /** Kill and restart ffmpeg when no progress arrives for this long while live. */
  stallTimeoutSec: number
  /** Probe cameras before each (re)start and build the wall from the ones that answer. */
  skipDeadCameras: boolean
  /** Keep a dead camera's tile in the wall as an "OFFLINE" placeholder instead of re-gridding. */
  placeholderTiles: boolean
  /** Write a low-rate JPEG of the composed output so the UI can show what viewers see. */
  previewEnabled: boolean
  /** When cameras were skipped, re-probe them this often and rebuild the wall on recovery. 0 = off. */
  rescanMinutes: number

  alerts: AlertsConfig

  /** Automatic posts into the YouTube live chat, driven by metric values. */
  chatAlerts: ChatAlertsConfig

  /**
   * HMI-style widgets drawn into the broadcast, bound to live metric values.
   *
   * Only the design lives here. Where the values come from is per-site
   * infrastructure carrying a password, and this object is pushed down from the
   * console and reported back up — so the endpoint is read from a local file
   * instead. See shared/overlay.ts and main/metricsSource.ts.
   */
  overlay: OverlayConfig

  /** Start and stop the stream automatically inside a daily window. */
  scheduleEnabled: boolean
  /** Local wall-clock times, 'HH:MM'. An end at or before the start spans midnight. */
  scheduleStart: string
  scheduleEnd: string
  /** Days the window applies to, 0 = Sunday … 6 = Saturday, keyed on the day the window opens. */
  scheduleDays: number[]

  /** UI colour scheme. */
  theme: ThemeMode
  /** Start streaming as soon as the app launches (kiosk / unattended operation). */
  autoStart: boolean
  /** Register the app to launch when the user logs in to Windows. */
  launchAtLogin: boolean
  /** Closing the window hides to the system tray instead of quitting. */
  closeToTray: boolean
  /** Launch hidden in the tray — pairs with launchAtLogin + autoStart. */
  startMinimized: boolean
  /** Absolute path to ffmpeg.exe, or just "ffmpeg" to use PATH. */
  ffmpegPath: string

  /** Keep a copy on disk of exactly what goes out to the platforms. */
  recording: RecordingConfig

  /** Keep what the tags read, so a recording can be annotated with the past. */
  metricHistory: MetricHistoryConfig
}

/**
 * Writing down what the tags read, so a recording can be replayed with them.
 *
 * On by default, unlike recording itself, and the difference is deliberate.
 * Recording is off until asked for because it consumes tens of gigabytes on an
 * unattended machine. This consumes kilobytes: only changes are written, and a
 * site whose gate has not moved in an hour costs nothing for that hour. The
 * asymmetry that matters is on the other side — footage not recorded can be
 * recorded tomorrow, but a reading not written down is gone for good, and the
 * operator only discovers they wanted it when they are looking at the video of
 * the day something went wrong.
 */
export interface MetricHistoryConfig {
  enabled: boolean
  /**
   * Seconds between samples. Not the poll rate: the overlay reads far faster
   * than anything worth storing changes, and a gauge laid over video does not
   * need sub-second resolution.
   */
  sampleSec: number
  /**
   * Days of history to keep. 0 follows the recording's own age limit, which is
   * the sensible default — history outliving the video it annotates is dead
   * weight, and video outliving its history replays blank.
   */
  keepDays: number
  /**
   * Tags to record beyond those already in use.
   *
   * By default this records what the site is already reading — the overlay's
   * tags and the chat rules' — because those are known to matter and cost
   * nothing extra to sample. The gap that leaves is real and worth stating: a
   * tag nobody displays today has no history to replay tomorrow, and the
   * operator finds that out at the exact moment they wanted it. This is how
   * they get ahead of it, by naming tags to keep whether or not anything
   * currently draws them.
   */
  tags: string[]
}

/**
 * Recording the broadcast to local disk.
 *
 * The copy is taken from the same encoded packets the platforms receive, as a
 * second branch of the tee that is already there for multi-destination output.
 * That is the whole reason this costs nothing: no second encode, and the file
 * is the broadcast rather than something merely similar to it.
 *
 * Two properties matter more than any setting here, and both were measured
 * against a real ffmpeg rather than assumed:
 *
 *  - A failing disk must never take the broadcast down. The branch carries
 *    `onfail=ignore` behind its own fifo, so a directory that is unwritable,
 *    full, or deleted mid-run costs the recording and nothing else. Verified by
 *    removing the directory while running: the live output stayed complete.
 *
 *  - Segments are written as MPEG-TS, not MP4. MP4 writes its index when the
 *    file closes, so a power cut leaves a file of the right size that decodes
 *    to zero frames — losing precisely the hour an operator would want to look
 *    at. A killed TS file plays to its last written byte. Closed segments are
 *    remuxed to MP4 afterwards (a stream copy) so what an operator opens is
 *    ordinary and double-clickable, while the segment still being written is
 *    always the crash-proof one.
 */
export interface RecordingConfig {
  enabled: boolean
  /** Folder holding the segments. Empty means the default under the data dir. */
  dir: string
  /** Minutes per file. Cutting happens on a keyframe, so files land near this. */
  segmentMinutes: number
  /** Delete segments older than this many days. 0 disables the age rule. */
  keepDays: number
  /**
   * Delete the oldest segments once the folder exceeds this. 0 disables it.
   *
   * Both limits run, and whichever bites first wins. An age limit alone cannot
   * protect the disk — add cameras or raise the bitrate and seven days stops
   * fitting — and a full disk is a worse outcome than a short history, because
   * it takes down the machine the broadcast is running on.
   */
  keepGb: number
}

/**
 * One segment on disk, as the gallery sees it.
 *
 * Lives in shared rather than beside the code that produces it because the
 * renderer has to name this shape and must not reach into a main-process
 * module to do it. No absolute paths cross to the UI: the id is the handle for
 * every operation, and the main process resolves it back to a file — a
 * renderer that cannot name an arbitrary path cannot ask for one to be opened
 * or deleted.
 */
export interface Recording {
  /** Base name without extension — the id every call passes back. */
  id: string
  /** Wall-clock start, parsed from the name. */
  startedAt: string
  bytes: number
  /** Seconds, derived from when the next segment started. Null when unknown. */
  durationSec: number | null
  /** Whether a poster frame has been taken; fetched separately, by id. */
  hasThumb: boolean
  /** True while ffmpeg still holds it — shown as recording now, never deleted. */
  inProgress: boolean
  /** Operator's name for it, from the sidecar. Empty means show the clock. */
  title: string
  /** Retention will not delete this one. See RecordingMeta.pinned. */
  pinned: boolean
}

/**
 * Per-recording settings, stored in a sidecar beside the segment.
 *
 * Distinct from RecordingConfig, which governs how recording happens at all.
 * This is what one operator decided about one hour of footage.
 */
export interface RecordingMeta {
  /** Human name. "The flood on the 3rd" beats a timestamp when searching. */
  title: string
  notes: string
  /**
   * Retention skips this segment entirely — both the age pass and the size pass.
   *
   * The escape hatch for the thing retention exists to prevent: the footage
   * somebody actually needs ageing out on a timer. It is deliberately absolute
   * rather than a longer expiry, because "keep this" is what an operator means
   * and a second deadline is just a slower way to lose it. The cost is real
   * and is surfaced rather than hidden: pinned bytes cannot be reclaimed, so a
   * folder pinned past its size limit reports that it can no longer hold the
   * limit instead of silently overrunning the disk.
   */
  pinned: boolean
  /** In-app player only; never affects the file or an export. */
  playback: {
    /** 0.25–4. Outside that range browsers drop the audio silently. */
    speed: number
    muted: boolean
    loop: boolean
  }
  /**
   * Seconds from the start of the segment. Null means "from the beginning" /
   * "to the end". Trimming on these bounds is a stream copy and costs nothing;
   * it is the only edit here that does not require a re-encode.
   */
  trim: { inSec: number | null; outSec: number | null }
  /** Export target. Null means "same as the source", which stays a stream copy. */
  export: {
    width: number | null
    height: number | null
    bitrateKbps: number | null
    container: 'mp4' | 'mkv'
  }
}

/** What the Recordings tab loads in one call: the folder, and what is in it. */
export interface RecordingsView {
  /** Absolute folder, shown so an operator knows which disk is filling up. */
  dir: string
  /** Total bytes held, posters included. */
  bytes: number
  /** Newest first. */
  items: Recording[]
  /** Set when the folder cannot be read at all; the tab says so plainly. */
  error: string | null
}

export interface HistoryPoint {
  /** Epoch ms — the same clock a recording's startedAt is on. */
  t: number
  /** Only the tags that changed at this instant, except at a keyframe. */
  values: Record<string, string>
}

export interface HistoryWindow {
  from: number
  to: number
  /** Every tag seen in the window. */
  tags: string[]
  /**
   * The reading of every tag as of `from`, reconstructed from the last
   * keyframe and the deltas after it. Without this a window that opens in the
   * middle of a steady period would start with no values at all.
   */
  initial: Record<string, string>
  /** Changes within the window, oldest first. */
  points: HistoryPoint[]
  /** True when points were dropped to fit MAX_POINTS. */
  downsampled: boolean
}

/**
 * A video built from scratch rather than annotated onto a recording.
 *
 * Deliberately the same timeline model as a recording's edit: `annotations`
 * carries everything drawn — and a `video` annotation filling the frame is a
 * clip, while one that does not is an inset — and `audio` carries everything
 * heard. A project is therefore an empty stage plus that model, not a second
 * editor with its own shapes to keep in step.
 */
export interface Project {
  id: string
  name: string
  createdAt: string
  updatedAt: string
  /** Canvas the composition is authored against; geometry stays normalised. */
  width: number
  height: number
  /** How long the finished video runs. The timeline spans exactly this. */
  durationSec: number
  annotations: Annotation[]
  audio: AudioClip[]
}

/** What the picker lists, without loading every project's whole timeline. */
export interface ProjectSummary {
  id: string
  name: string
  updatedAt: string
  durationSec: number
  /** Everything placed on the timeline, drawn or heard. */
  clips: number
}

/**
 * One export, as the UI watches it.
 *
 * Queued jobs are shown as well as running ones, because an export can wait
 * behind another for an hour and a UI that showed nothing would look broken.
 */
export interface ExportJob {
  id: string
  /** What it came from, in the operator's words. */
  name: string
  state: 'queued' | 'running' | 'done' | 'failed' | 'cancelled'
  /** 0-100. Stays 0 when the output length is not known in advance. */
  percent: number
  /** Absolute path of the file being produced. */
  output: string
  startedAt: string
  finishedAt: string | null
  error: string | null
  /** Whether this is a copy or a re-encode, and what that costs. */
  reason: string
  /** Seconds of output written so far. */
  secondsDone?: number
  bytes?: number
}

export const DEFAULT_CONFIG: StreamConfig = {
  cameras: [
    blankCamera('cam-1', 'Camera 1', {
      url: 'rtsp://user:password@192.168.1.10:554/unicast/c1/s0/live'
    })
  ],
  rtspTransport: 'tcp',
  hwDecode: '',
  mode: 'grid',
  layout: 'auto',
  gridAlignH: 'center',
  gridAlignV: 'center',
  mainCameraIndex: 0,
  stripPosition: 'bottom',
  stripAlign: 'middle',
  thumbScale: 0.18,
  thumbGap: 10,
  thumbMargin: 16,
  thumbBorder: 2,
  spotlightRects: {},
  spotlightOrder: [],
  outputWidth: 1920,
  outputHeight: 1080,
  tileFit: 'contain',
  tileFitAnchor: 'center',
  showLabels: true,

  destinations: [
    {
      id: 'dest-1',
      name: 'YouTube Live',
      kind: 'youtube',
      url: DESTINATION_PRESETS.youtube.url,
      streamKey: '',
      enabled: true,
      useBackup: false,
      cameraId: null,
      segmentHours: 0,
      overrides: { ...NO_OVERRIDES }
    }
  ],

  videoMode: 'encode',
  encoder: 'libx264',
  preset: 'veryfast',
  videoBitrateKbps: 5000,
  bufsizeKbps: 10000,
  gop: 60,
  fps: 30,

  audioMode: 'silent',
  audioCameraIndex: 0,
  audioFilePath: '',
  audioFolderPath: '',
  audioVizPosition: 'off',
  audioVizSize: 'medium',
  audioVizOpacity: 1,
  audioBitrateKbps: 128,
  sampleRate: 44100,

  autoRestart: true,
  restartDelaySec: 5,
  segmentGapSec: 90,
  maxRestartDelaySec: 60,
  stallTimeoutSec: 20,
  skipDeadCameras: true,
  placeholderTiles: true,
  previewEnabled: true,
  rescanMinutes: 2,

  alerts: {
    telegram: { enabled: false, botToken: '', chatId: '' },
    discord: { enabled: false, webhookUrl: '' },
    webhook: { enabled: false, url: '' },
    downThresholdSec: 30,
    cooldownSec: 300,
    notifyManualStartStop: false
  },

  chatAlerts: { ...DEFAULT_CHAT_ALERTS, rules: [] },

  overlay: { ...DEFAULT_OVERLAY, widgets: [] },

  scheduleEnabled: false,
  scheduleStart: '08:00',
  scheduleEnd: '20:00',
  scheduleDays: [0, 1, 2, 3, 4, 5, 6],

  theme: 'light',

  autoStart: false,
  launchAtLogin: false,
  closeToTray: true,
  startMinimized: false,
  ffmpegPath: 'ffmpeg',
  // Off by default: recording consumes disk on a machine the operator is not
  // standing in front of, and a feature that fills a 24/7 box's disk without
  // being asked for is worse than one nobody turned on.
  recording: {
    enabled: false,
    dir: '',
    segmentMinutes: 60,
    keepDays: 7,
    keepGb: 200
  },
  // On by default: see MetricHistoryConfig. Costs kilobytes, and the loss from
  // having it off is the one kind that cannot be undone later.
  metricHistory: {
    enabled: true,
    sampleSec: 2,
    keepDays: 0,
    tags: []
  }
}

export function enabledCameras(config: StreamConfig): CameraSource[] {
  return config.cameras.filter((c) => c.enabled && cameraReady(c))
}

/**
 * Destinations that are actually pushable. A custom RTMP endpoint may carry the
 * key inside the path, so only the managed kinds require a separate key.
 */
export function enabledDestinations(config: StreamConfig): StreamDestination[] {
  return config.destinations.filter(
    (d) => d.enabled && d.url.trim() && (d.kind === 'custom' || d.streamKey.trim())
  )
}

/** The gate every "can we go live?" check shares. */
export function hasUsableDestination(config: StreamConfig): boolean {
  return enabledDestinations(config).length > 0
}

export type StreamState = 'idle' | 'starting' | 'live' | 'reconnecting' | 'stopping'

export interface StreamStatus {
  state: StreamState
  /** Epoch ms when the current ffmpeg process started, null when not running. */
  startedAt: number | null
  restarts: number
  lastError: string | null
  /** Names of cameras that failed pre-flight and were left out of the wall. */
  skipped: string[]
  /** Expanded output names whose tee slave died while the rest kept streaming. */
  destinationsDown: string[]
  /** Parsed from ffmpeg's progress line. */
  stats: StreamStats | null
  /**
   * Digest of the overlay graph the running process was started with, or null
   * when it is not drawing one.
   *
   * The Designer compares this against the draft being edited: a difference is
   * exactly the set of changes the running encoder cannot pick up on its own,
   * and so the only ones worth offering a restart for.
   */
  overlayFingerprint?: string | null
}

export interface StreamStats {
  fps: number
  bitrateKbps: number
  /** Encoded timestamp in seconds. */
  timeSec: number
  droppedFrames: number
  speed: number
}

export interface ProbeResult {
  ok: boolean
  error: string | null
  video: { codec: string; width: number; height: number; fps: number } | null
  audio: { codec: string; channels: number; sampleRate: number } | null
}

/** Probe result tagged with the camera it belongs to. */
export interface CameraProbe extends ProbeResult {
  id: string
}

export interface LogLine {
  ts: number
  level: 'info' | 'error'
  text: string
}

/** One channel's outcome from the Alerts card's "Send test" button. */
export interface AlertTestResult {
  channel: 'telegram' | 'discord' | 'webhook'
  ok: boolean
  detail: string
}

/** A recorded state change, replayed by the uptime dashboard. */
export interface HistoryTransition {
  ts: number
  state: StreamState
  note?: string
}

/** Once-a-minute snapshot of the live telemetry. */
export interface HistorySample {
  ts: number
  bitrateKbps: number
  speed: number
}

export interface UptimeReport {
  /** Window the report covers, in hours. */
  hours: number
  /** Share of the window spent live, 0–100. */
  uptimePct: number
  downtimeSec: number
  restartCount: number
  transitions: HistoryTransition[]
  samples: HistorySample[]
}

/** Snapshot of the composed output, written by ffmpeg and read back over IPC. */
export interface PreviewFrame {
  dataUrl: string
  mtime: number
}

/** Source-preview mode: composing the wall locally, without streaming it anywhere. */
export interface PreviewState {
  running: boolean
  /** Cameras that failed pre-flight and are showing as OFFLINE tiles. */
  skipped: string[]
  error: string | null
}
